import React, { useState, useEffect } from 'react';
import { 
  User, Phone, Mail, Lock, LogOut, Package, Calendar, 
  Clock, CheckCircle2, XCircle, PlayCircle, Send, Eye, EyeOff,
  FileText, Download, ArrowLeft, ChevronRight, ShoppingBag, MapPin, Building, Search, Loader2, Check, AlertCircle
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { useCustomer } from '../context/CustomerContext';
import { 
  getCustomerOrders, getCustomerOrderDetail,
  lookupCep, lookupCnpj, updateCustomerProfile
} from '../services/api';
import { validateCpfCnpj, formatCpfCnpj, formatCEP } from '../utils/validators';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const CustomerAreaContent = ({ onOpenLoginModal }) => {
  const { customer, token, isLoggedIn, logout } = useCustomer();
  const [orders, setOrders] = useState([]);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('orders'); // 'orders' or 'account'
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [cpfCnpjError, setCpfCnpjError] = useState('');
  
  // Form states
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    email: '',
    password: '',
    cpf_cnpj: '',
    cep: '',
    street: '',
    number: '',
    complement: '',
    neighborhood: '',
    city: '',
    state: ''
  });
  
  // Lookup states
  const [lookingUpCep, setLookingUpCep] = useState(false);
  const [lookingUpCnpj, setLookingUpCnpj] = useState(false);
  const [savingProfile, setSavingProfile] = useState(false);

  useEffect(() => {
    if (isLoggedIn && customer) {
      populateFormFromCustomer(customer);
      fetchOrders();
    }
    setLoading(false);
  }, [isLoggedIn, customer]);

  const populateFormFromCustomer = (customerData) => {
    setFormData({
      name: customerData.name || '',
      phone: customerData.phone || '',
      email: customerData.email || '',
      password: '',
      cpf_cnpj: customerData.cpf_cnpj || '',
      cep: customerData.cep || '',
      street: customerData.street || '',
      number: customerData.number || '',
      complement: customerData.complement || '',
      neighborhood: customerData.neighborhood || '',
      city: customerData.city || '',
      state: customerData.state || ''
    });
  };

  const fetchOrders = async () => {
    if (!token) return;
    try {
      const ordersData = await getCustomerOrders(token);
      setOrders(ordersData);
    } catch (error) {
      console.error('Error fetching orders:', error);
    }
  };

  const handleLogout = async () => {
    await logout();
    window.location.href = '/';
  };

  // Validate CPF/CNPJ on blur
  const handleCpfCnpjBlur = () => {
    if (!formData.cpf_cnpj) {
      setCpfCnpjError('');
      return;
    }
    
    const validation = validateCpfCnpj(formData.cpf_cnpj);
    if (validation.type === 'incomplete') {
      setCpfCnpjError(validation.message);
    } else if (!validation.valid && validation.type) {
      setCpfCnpjError(`${validation.type.toUpperCase()} inválido. Verifique os dígitos.`);
    } else if (!validation.valid) {
      setCpfCnpjError('CPF deve ter 11 dígitos ou CNPJ deve ter 14 dígitos');
    } else {
      setCpfCnpjError('');
    }
  };

  // Handle CPF/CNPJ change
  const handleCpfCnpjChange = (value) => {
    const formatted = formatCpfCnpj(value);
    setFormData({ ...formData, cpf_cnpj: formatted });
    // Clear error while typing
    if (cpfCnpjError) {
      setCpfCnpjError('');
    }
  };

  // Format CPF/CNPJ (keep local for formatting)
  const formatCpfCnpjLocal = (value) => {
    const digits = value.replace(/\D/g, '');
    if (digits.length <= 11) {
      return digits
        .replace(/(\d{3})(\d)/, '$1.$2')
        .replace(/(\d{3})(\d)/, '$1.$2')
        .replace(/(\d{3})(\d{1,2})$/, '$1-$2');
    } else {
      return digits
        .replace(/^(\d{2})(\d)/, '$1.$2')
        .replace(/^(\d{2})\.(\d{3})(\d)/, '$1.$2.$3')
        .replace(/\.(\d{3})(\d)/, '.$1/$2')
        .replace(/(\d{4})(\d)/, '$1-$2')
        .substring(0, 18);
    }
  };

  // Format CEP
  const formatCep = (value) => {
    const digits = value.replace(/\D/g, '');
    return digits.replace(/(\d{5})(\d)/, '$1-$2').substring(0, 9);
  };

  // Lookup CEP
  const handleCepLookup = async () => {
    const cep = formData.cep.replace(/\D/g, '');
    if (cep.length !== 8) {
      setError('CEP deve ter 8 dígitos');
      return;
    }

    setLookingUpCep(true);
    setError('');
    
    try {
      const data = await lookupCep(cep);
      setFormData(prev => ({
        ...prev,
        street: data.street || prev.street,
        neighborhood: data.neighborhood || prev.neighborhood,
        city: data.city || prev.city,
        state: data.state || prev.state
      }));
      setSuccess('Endereço preenchido automaticamente!');
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.response?.data?.detail || 'Erro ao buscar CEP');
    } finally {
      setLookingUpCep(false);
    }
  };

  // Lookup CNPJ
  const handleCnpjLookup = async () => {
    const cnpj = formData.cpf_cnpj.replace(/\D/g, '');
    if (cnpj.length !== 14) {
      setError('CNPJ deve ter 14 dígitos');
      return;
    }

    setLookingUpCnpj(true);
    setError('');
    
    try {
      const data = await lookupCnpj(cnpj);
      setFormData(prev => ({
        ...prev,
        name: data.fantasy_name || data.name || prev.name,
        email: data.email || prev.email,
        cep: data.cep || prev.cep,
        street: data.street || prev.street,
        number: data.number || prev.number,
        complement: data.complement || prev.complement,
        neighborhood: data.neighborhood || prev.neighborhood,
        city: data.city || prev.city,
        state: data.state || prev.state
      }));
      setSuccess('Dados do CNPJ preenchidos automaticamente!');
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.response?.data?.detail || 'Erro ao buscar CNPJ');
    } finally {
      setLookingUpCnpj(false);
    }
  };

  // Save profile
  const handleSaveProfile = async (e) => {
    e.preventDefault();
    
    // Validate CPF/CNPJ before saving
    if (formData.cpf_cnpj) {
      const validation = validateCpfCnpj(formData.cpf_cnpj);
      if (!validation.valid) {
        const docType = validation.type === 'cpf' ? 'CPF' : validation.type === 'cnpj' ? 'CNPJ' : 'CPF/CNPJ';
        setCpfCnpjError(`${docType} inválido. Verifique os dígitos.`);
        setError(`${docType} inválido. Corrija antes de salvar.`);
        return;
      }
    }
    
    setSavingProfile(true);
    setError('');
    
    try {
      // Normalize CPF/CNPJ - send only digits
      const cpfCnpjDigits = formData.cpf_cnpj ? formData.cpf_cnpj.replace(/\D/g, '') : null;
      
      const updateData = {
        name: formData.name,
        email: formData.email || null,
        cpf_cnpj: cpfCnpjDigits,
        cep: formData.cep ? formData.cep.replace(/\D/g, '') : null,
        street: formData.street || null,
        number: formData.number || null,
        complement: formData.complement || null,
        neighborhood: formData.neighborhood || null,
        city: formData.city || null,
        state: formData.state || null
      };
      
      if (formData.password) {
        updateData.password = formData.password;
      }
      
      await updateCustomerProfile(token, updateData);
      setFormData(prev => ({ ...prev, password: '' }));
      setSuccess('Dados atualizados com sucesso!');
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.response?.data?.detail || 'Erro ao atualizar dados');
    } finally {
      setSavingProfile(false);
    }
  };

  const formatPhone = (phone) => {
    if (!phone) return '';
    const digits = phone.replace(/\D/g, '');
    if (digits.length === 11) {
      return `(${digits.slice(0, 2)}) ${digits.slice(2, 7)}-${digits.slice(7)}`;
    }
    return phone;
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatusInfo = (status) => {
    const statusMap = {
      'pendente': { label: 'Pendente', color: 'bg-yellow-100 text-yellow-700', bgDot: 'bg-yellow-400', icon: Clock },
      'em_andamento': { label: 'Em Andamento', color: 'bg-blue-100 text-blue-700', bgDot: 'bg-blue-400', icon: PlayCircle },
      'aprovado': { label: 'Aprovado', color: 'bg-green-100 text-green-700', bgDot: 'bg-green-400', icon: CheckCircle2 },
      'enviado': { label: 'Enviado', color: 'bg-purple-100 text-purple-700', bgDot: 'bg-purple-400', icon: Send },
      'concluido': { label: 'Concluído', color: 'bg-emerald-100 text-emerald-700', bgDot: 'bg-emerald-400', icon: CheckCircle2 },
      'cancelado': { label: 'Cancelado', color: 'bg-red-100 text-red-700', bgDot: 'bg-red-400', icon: XCircle }
    };
    return statusMap[status] || { label: status || 'Novo', color: 'bg-slate-100 text-slate-700', bgDot: 'bg-slate-400', icon: Clock };
  };

  const isCnpj = formData.cpf_cnpj.replace(/\D/g, '').length > 11;

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="text-center">
          <div className="w-12 h-12 border-3 border-[#195C75] border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-slate-500 font-medium">Carregando...</p>
        </div>
      </div>
    );
  }

  // Not logged in - show login prompt
  if (!isLoggedIn) {
    return (
      <div className="max-w-lg mx-auto py-12 px-4">
        <div className="text-center">
          <div className="w-20 h-20 bg-slate-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <User className="w-10 h-10 text-slate-400" />
          </div>
          <h2 className="text-2xl font-bold text-slate-800 mb-2">Área do Cliente</h2>
          <p className="text-slate-500 mb-6">
            Faça login para acessar seus pedidos e dados cadastrais.
          </p>
          <Button 
            onClick={onOpenLoginModal}
            className="bg-[#195C75] hover:bg-[#195C75]/90 text-white px-8 py-3"
          >
            Entrar na minha conta
          </Button>
        </div>
      </div>
    );
  }

  // Order Detail View
  if (selectedOrder) {
    const statusInfo = getStatusInfo(selectedOrder.status);
    const StatusIcon = statusInfo.icon;
    
    return (
      <div className="max-w-4xl mx-auto py-6 px-4">
        <button
          onClick={() => setSelectedOrder(null)}
          className="flex items-center gap-2 text-slate-500 hover:text-[#195C75] mb-4"
        >
          <ArrowLeft className="w-4 h-4" />
          Voltar aos pedidos
        </button>

        {/* Order Info Card */}
        <div className="bg-white rounded-xl shadow-sm p-6 mb-4">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h2 className="text-xl font-semibold text-slate-800">Pedido #{selectedOrder.id?.slice(0, 8)}</h2>
              <p className="text-sm text-slate-500">{formatDate(selectedOrder.created_at)}</p>
            </div>
            <span className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-medium ${statusInfo.color}`}>
              <StatusIcon className="w-4 h-4" />
              {statusInfo.label}
            </span>
          </div>

          {/* Items */}
          <div className="border-t border-slate-200 pt-4">
            <h3 className="font-medium text-slate-800 mb-3">Itens do Pedido</h3>
            <div className="space-y-3">
              {selectedOrder.items?.map((item, index) => (
                <div key={index} className="flex items-center gap-3 p-3 bg-slate-50 rounded-lg">
                  {item.image_url && (
                    <img 
                      src={item.image_url} 
                      alt={item.product_name}
                      className="w-12 h-12 object-cover rounded-lg"
                    />
                  )}
                  <div className="flex-1">
                    <p className="font-medium text-slate-800">{item.product_name}</p>
                    <p className="text-sm text-slate-500">Quantidade: {item.quantity}</p>
                    {item.customization_notes && (
                      <p className="text-sm text-slate-500">Obs: {item.customization_notes}</p>
                    )}
                  </div>
                  {item.unit_price && (
                    <div className="text-right">
                      <p className="text-sm text-slate-500">R$ {item.unit_price?.toFixed(2)} un.</p>
                      <p className="font-medium text-[#195C75]">
                        R$ {(item.unit_price * item.quantity).toFixed(2)}
                      </p>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* Total */}
          {selectedOrder.items?.some(item => item.unit_price) && (
            <div className="border-t border-slate-200 mt-4 pt-4">
              <div className="flex justify-between items-center">
                <span className="font-medium text-slate-700">Total Estimado:</span>
                <span className="text-xl font-bold text-[#195C75]">
                  R$ {selectedOrder.items.reduce((acc, item) => acc + (item.unit_price || 0) * item.quantity, 0).toFixed(2)}
                </span>
              </div>
            </div>
          )}

          {/* Notes */}
          {selectedOrder.notes && (
            <div className="border-t border-slate-200 mt-4 pt-4">
              <h3 className="font-medium text-slate-800 mb-2">Observações</h3>
              <p className="text-slate-600">{selectedOrder.notes}</p>
            </div>
          )}
        </div>

        {/* Download PDF */}
        <Button
          onClick={() => window.open(`${API_URL}/api/quotes/${selectedOrder.id}/pdf`, '_blank')}
          variant="outline"
          className="w-full py-3 rounded-xl"
        >
          <Download className="w-5 h-5 mr-2" />
          Baixar PDF do Orçamento
        </Button>
      </div>
    );
  }

  // Main Customer Area (Logged In)
  return (
    <div className="max-w-3xl mx-auto py-4 px-4">
      {/* Tabs - Compact */}
      <div className="bg-white rounded-lg shadow-sm mb-4">
        <div className="flex">
          <button
            onClick={() => setActiveTab('orders')}
            className={`flex-1 py-2.5 text-xs font-medium border-b-2 transition-colors ${
              activeTab === 'orders'
                ? 'border-[#195C75] text-[#195C75]'
                : 'border-transparent text-slate-500 hover:text-slate-700'
            }`}
          >
            <Package className="w-3.5 h-3.5 inline mr-1.5" />
            Meus Pedidos
          </button>
          <button
            onClick={() => setActiveTab('account')}
            className={`flex-1 py-2.5 text-xs font-medium border-b-2 transition-colors ${
              activeTab === 'account'
                ? 'border-[#195C75] text-[#195C75]'
                : 'border-transparent text-slate-500 hover:text-slate-700'
            }`}
          >
            <User className="w-3.5 h-3.5 inline mr-1.5" />
            Minha Conta
          </button>
        </div>
      </div>

      {/* Success/Error Messages */}
      {error && (
        <div className="mb-3 p-2 bg-red-50 border border-red-200 rounded-lg text-red-600 text-xs">
          {error}
        </div>
      )}
      {success && (
        <div className="mb-3 p-2 bg-green-50 border border-green-200 rounded-lg text-green-600 text-xs flex items-center gap-1">
          <Check className="w-3 h-3" />
          {success}
        </div>
      )}

      {/* Orders Tab */}
      {activeTab === 'orders' && (
        <>
          {orders.length === 0 ? (
            <div className="bg-white rounded-xl shadow-sm p-8 text-center">
              <ShoppingBag className="w-12 h-12 mx-auto text-slate-300 mb-3" />
              <h3 className="font-medium text-slate-700 mb-1">Nenhum pedido</h3>
              <p className="text-sm text-slate-500 mb-3">Você ainda não fez nenhum orçamento.</p>
              <a href="/">
                <Button size="sm" className="bg-[#195C75] hover:bg-[#195C75]/90">
                  Ver Catálogo
                </Button>
              </a>
            </div>
          ) : (
            <div className="bg-white rounded-xl shadow-sm overflow-hidden">
              {orders.map((order, idx) => {
                const statusInfo = getStatusInfo(order.status);
                const StatusIcon = statusInfo.icon;
                const total = order.items?.reduce((acc, item) => acc + (item.unit_price || 0) * item.quantity, 0) || 0;
                const itemCount = order.items?.reduce((acc, item) => acc + item.quantity, 0) || 0;
                
                return (
                  <button
                    key={order.id}
                    onClick={() => setSelectedOrder(order)}
                    className={`w-full flex items-center gap-3 px-4 py-3 hover:bg-slate-50 transition-colors text-left ${idx > 0 ? 'border-t border-slate-100' : ''}`}
                  >
                    {/* Status indicator */}
                    <div className={`w-2 h-2 rounded-full flex-shrink-0 ${statusInfo.bgDot || 'bg-yellow-400'}`} />
                    
                    {/* Info */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="font-medium text-slate-800 text-sm">{itemCount} {itemCount === 1 ? 'item' : 'itens'}</span>
                        <span className="text-xs text-slate-400">#{order.id?.slice(0, 8)}</span>
                      </div>
                    </div>
                    
                    {/* Status Badge - smaller */}
                    <span className={`text-[10px] px-2 py-0.5 rounded-full font-medium ${statusInfo.color}`}>
                      {statusInfo.label}
                    </span>
                    
                    {/* Total */}
                    {total > 0 && (
                      <span className="font-semibold text-[#195C75] text-sm w-20 text-right">
                        R$ {total.toFixed(2)}
                      </span>
                    )}
                    
                    {/* Date */}
                    <span className="text-xs text-slate-400 w-24 text-right hidden sm:block">
                      {formatDate(order.created_at)}
                    </span>
                    
                    <ChevronRight className="w-4 h-4 text-slate-300 flex-shrink-0" />
                  </button>
                );
              })}
            </div>
          )}
        </>
      )}

      {/* Account Tab */}
      {activeTab === 'account' && (
        <form onSubmit={handleSaveProfile} className="bg-white rounded-xl shadow-sm">
          {/* Dados Pessoais */}
          <div className="p-4 border-b border-slate-100">
            <h3 className="text-sm font-semibold text-slate-600 mb-3 flex items-center gap-2">
              <User className="w-4 h-4 text-[#195C75]" />
              Dados Pessoais
            </h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-medium text-slate-500 mb-1">Nome completo</label>
                <Input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="h-9 text-sm"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-slate-500 mb-1">Telefone</label>
                <Input
                  type="tel"
                  value={formData.phone}
                  disabled
                  className="h-9 text-sm bg-slate-50"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-slate-500 mb-1">E-mail</label>
                <Input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  placeholder="seu@email.com"
                  className="h-9 text-sm"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-slate-500 mb-1">Nova senha</label>
                <div className="relative">
                  <Input
                    type={showPassword ? 'text' : 'password'}
                    value={formData.password}
                    onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                    placeholder="Deixe em branco para manter"
                    className="h-9 text-sm pr-8"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                  >
                    {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>
            </div>
          </div>

          {/* Documento */}
          <div className="p-4 border-b border-slate-100">
            <h3 className="text-sm font-semibold text-slate-600 mb-3 flex items-center gap-2">
              <FileText className="w-4 h-4 text-[#195C75]" />
              Documento
            </h3>
            <div className="flex gap-2">
              <div className="relative flex-1">
                <Input
                  type="text"
                  placeholder="CPF ou CNPJ"
                  value={formData.cpf_cnpj}
                  onChange={(e) => handleCpfCnpjChange(e.target.value)}
                  onBlur={handleCpfCnpjBlur}
                  className={`h-9 text-sm ${cpfCnpjError ? 'border-red-500' : ''}`}
                  maxLength={18}
                />
              </div>
              {isCnpj && !cpfCnpjError && (
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={handleCnpjLookup}
                  disabled={lookingUpCnpj}
                  className="h-9 px-2"
                >
                  {lookingUpCnpj ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
                </Button>
              )}
            </div>
            {cpfCnpjError && (
              <p className="text-[10px] text-red-500 mt-1 flex items-center gap-1">
                <AlertCircle className="w-3 h-3" /> {cpfCnpjError}
              </p>
            )}
          </div>

          {/* Endereço */}
          <div className="p-4">
            <h3 className="text-sm font-semibold text-slate-600 mb-3 flex items-center gap-2">
              <MapPin className="w-4 h-4 text-[#195C75]" />
              Endereço
            </h3>
            <div className="grid grid-cols-6 gap-2">
              {/* CEP */}
              <div className="col-span-2">
                <label className="block text-xs font-medium text-slate-500 mb-1">CEP</label>
                <div className="flex gap-1">
                  <Input
                    type="text"
                    placeholder="00000-000"
                    value={formData.cep}
                    onChange={(e) => setFormData({ ...formData, cep: formatCep(e.target.value) })}
                    className="h-9 text-sm"
                    maxLength={9}
                  />
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={handleCepLookup}
                    disabled={lookingUpCep}
                    className="h-9 px-2"
                  >
                    {lookingUpCep ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
                  </Button>
                </div>
              </div>
              
              {/* Rua */}
              <div className="col-span-3">
                <label className="block text-xs font-medium text-slate-500 mb-1">Rua</label>
                <Input
                  type="text"
                  placeholder="Rua/Avenida"
                  value={formData.street}
                  onChange={(e) => setFormData({ ...formData, street: e.target.value })}
                  className="h-9 text-sm"
                />
              </div>
              
              {/* Número */}
              <div className="col-span-1">
                <label className="block text-xs font-medium text-slate-500 mb-1">Nº</label>
                <Input
                  type="text"
                  placeholder="Nº"
                  value={formData.number}
                  onChange={(e) => setFormData({ ...formData, number: e.target.value })}
                  className="h-9 text-sm"
                />
              </div>
              
              {/* Complemento */}
              <div className="col-span-2">
                <label className="block text-xs font-medium text-slate-500 mb-1">Complemento</label>
                <Input
                  type="text"
                  placeholder="Apto, sala..."
                  value={formData.complement}
                  onChange={(e) => setFormData({ ...formData, complement: e.target.value })}
                  className="h-9 text-sm"
                />
              </div>
              
              {/* Bairro */}
              <div className="col-span-2">
                <label className="block text-xs font-medium text-slate-500 mb-1">Bairro</label>
                <Input
                  type="text"
                  placeholder="Bairro"
                  value={formData.neighborhood}
                  onChange={(e) => setFormData({ ...formData, neighborhood: e.target.value })}
                  className="h-9 text-sm"
                />
              </div>
              
              {/* Cidade */}
              <div className="col-span-1">
                <label className="block text-xs font-medium text-slate-500 mb-1">Cidade</label>
                <Input
                  type="text"
                  placeholder="Cidade"
                  value={formData.city}
                  onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                  className="h-9 text-sm"
                />
              </div>
              
              {/* Estado */}
              <div className="col-span-1">
                <label className="block text-xs font-medium text-slate-500 mb-1">UF</label>
                <Input
                  type="text"
                  placeholder="UF"
                  value={formData.state}
                  onChange={(e) => setFormData({ ...formData, state: e.target.value.toUpperCase() })}
                  className="h-9 text-sm"
                  maxLength={2}
                />
              </div>
            </div>
          </div>

          {/* Save Button */}
          <div className="px-4 pb-4">
            <Button 
              type="submit"
              disabled={savingProfile || !!cpfCnpjError}
              className="w-full bg-[#195C75] hover:bg-[#195C75]/90 text-white h-10 rounded-lg text-sm disabled:opacity-50"
            >
              {savingProfile ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin mr-2" />
                  Salvando...
                </>
              ) : cpfCnpjError ? (
                'Corrija o CPF/CNPJ para salvar'
              ) : (
                'Salvar Alterações'
              )}
            </Button>
          </div>
        </form>
      )}
    </div>
  );
};

export default CustomerAreaContent;
